import React, { Component } from "react";
import PropTypes from "prop-types";
import { withRouter } from "react-router-dom";
import withStyles from "@mui/styles/withStyles";
import { Fab, Tooltip } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import Backend from "../common/utils/Backend";
import AddIcon from "@mui/icons-material/Add";
import autoBind from "react-autobind";

import CreateProjectDialog from "./dialogs/CreateProjectDialog";
import ProjectsTable from "./components/ProjectsTable";

import { authenticationService } from "../common/services";

/**
 * Adds leading zeros to a number
 * @param {Number} num Number
 * @param {Number} size length that the number should have
 * @returns {String} String of number with zero padding
 */
function pad(num, size) {
  var s = num + "";
  while (s.length < size) s = "0" + s;
  return s;
}

/**
 * Generates a default name for new Projects like "Project 001"
 * @param {Object[]} data List of Projects
 * @returns {String} Smart Default name for a new project
 */
function incrementDefaultName(data) {
  let newDefaultName = "Project 001";
  let maxProjNum = 0;
  for (let proj of data) {
    if (proj.name.match("Project [0-9][0-9][0-9]")) {
      const projNum = parseInt(proj.name.replace("Project ", ""), 10);
      let newMaxProjNum = Math.max(maxProjNum, projNum);
      if (newMaxProjNum) maxProjNum = newMaxProjNum;
    }
  }
  newDefaultName = `Project ${pad(maxProjNum + 1, 3)}`;
  return newDefaultName;
}

const styles = (theme) => ({
  root: { height: "100%", overflow: "hidden" },
  fab: {
    position: "absolute",
    bottom: theme.spacing(2),
    right: theme.spacing(2),
  },
});

class HomePage extends Component {
  constructor(props) {
    super(props);
    autoBind(this);
    this._isMounted = false;
    this.state = {
      editableProject: null,
      projects: [],
      open: false,
      newDefaultName: "Project 001",
      activeTab: 0,
      serverIsRunning: false,
      user: { group: {} },
    };
    this.lastProjectRefreshDate = null;

    Backend.getCurrentUser((user) => {
      if (user.fullName === null) {
        authenticationService.logout();
      } else {
        this.setMountedState({ user });
      }
    });
    this.checkPythonServer();
  }
  checkPythonServer() {
    Backend.isLocalServerReady((result) => {
      if (this.state.serverIsRunning !== result) {
        this.setMountedState({ serverIsRunning: result });
      }
      if (!result) {
        setTimeout(this.checkPythonServer, 2000);
      }
    });
  }

  setMountedState(stateObject, callback) {
    if (this._isMounted) {
      this.setState(stateObject, callback);
    }
  }

  handleClickOpen() {
    Backend.getLicensingInfo((license) => {
      if (license.licenseStatus === "VALID") {
        this.setMountedState({
          editableProject: null,
          open: true,
        });
      } else {
        authenticationService.logout();
        this.props.history.push("/licensing");
      }
    });
  }

  handleEditOpen(project) {
    this.setMountedState({
      editableProject: project,
      open: true,
    });
  }

  handleClose() {
    this.setMountedState({
      editableProject: null,
      open: false,
    });
    this.refreshProjects();
  }

  componentDidMount() {
    this._isMounted = true;
    this.refreshProjectsInterval();
    const activeTab = localStorage.getItem("activeProjectTab");
    if (activeTab) this.setMountedState({ activeTab: parseInt(activeTab) });
  }

  componentWillUnmount() {
    this._isMounted = false;
    clearInterval(this.refreshInterval);
  }

  refreshProjectsInterval() {
    this.refreshProjects(() => {
      setTimeout(() => this.refreshProjectsInterval(), 1000);
    });
  }

  /**
   * Refreshes the list of projects by fetching updated data from the backend.
   * If the component is not mounted, the function returns immediately.
   *
   * @param {Function} callback - A callback function to be executed after the projects are refreshed.
   *
   * The function performs the following steps:
   * 1. Checks if the component is mounted.
   * 2. Calls the backend to list projects with the last project refresh date.
   * 3. Updates the last project refresh date if a more recent change date is provided.
   * 4. Compares the fetched projects with the current state and updates the state if there are changes.
   * 5. Executes the callback function after the state is updated or if an error occurs.
   */
  refreshProjects(callback = () => {}) {
    if (!this._isMounted) return;

    Backend.listProjects(this.lastProjectRefreshDate).then(
      (data) => {
        if (!callback) return;
        const refreshProjectList = this.lastProjectRefreshDate === null;
        if (data.mostRecentChangeDateTime) {
          this.lastProjectRefreshDate = data.mostRecentChangeDateTime;
        }

        if (!data.projects || data.projects.length === 0) {
          callback();
          return;
        }

        const projectsChanged = refreshProjectList || data.projects.length > 0;
        if (!projectsChanged) {
          callback();
          return;
        }

        if (refreshProjectList) {
          this.setMountedState({
            projects: data.projects,
            newDefaultName: incrementDefaultName(data.projects),
          });
          callback();
          return;
        }

        let updatedProjects = [...this.state.projects];
        data.projects.forEach((newProject) => {
          const index = updatedProjects.findIndex(
            (project) => project.id === newProject.id,
          );
          if (index !== -1) {
            updatedProjects[index] = newProject;
          } else {
            updatedProjects.push(newProject);
          }
        });

        this.setMountedState({
          projects: updatedProjects,
          newDefaultName: incrementDefaultName(updatedProjects),
        });

        callback();
      },
      (error) => {
        if (this._isMounted && callback) {
          console.error(
            "Error loading projects, reloading page might help. Error: " +
              error,
          );
          callback();
        }
      },
    );
  }

  handleActiveTabChange(activeTabIndex) {
    if (this.state.activeTab !== activeTabIndex) {
      this.setMountedState({ activeTab: activeTabIndex });
      localStorage.setItem("activeProjectTab", activeTabIndex);
    }
  }

  onResetProjectList() {
    this.lastProjectRefreshDate = null;
    this.refreshProjects();
  }

  render() {
    const { classes } = this.props;
    const { serverIsRunning } = this.state;
    return (
      <div className={classes.root}>
        <ProjectsTable
          onEditProject={this.handleEditOpen.bind(this)}
          onResetProjectList={() => this.onResetProjectList()}
          projects={this.state.projects}
          activeTab={this.state.activeTab}
          handleActiveTabChange={this.handleActiveTabChange.bind(this)}
          serverIsRunning={serverIsRunning}
          user={this.state.user}
        />
        <Tooltip
          disableInteractive
          title={
            serverIsRunning ? "Add new project" : "Local API Server not ready!"
          }
        >
          <Fab
            className={classes.fab}
            color="primary"
            onClick={() => {
              if (serverIsRunning) {
                this.handleClickOpen();
              } else {
                window.showWarningSnackbar("Local API Server not ready!");
              }
            }}
          >
            {serverIsRunning ? (
              <AddIcon />
            ) : (
              <CircularProgress
                className={classes.serverSpinLoader}
                color="inherit"
              />
            )}
          </Fab>
        </Tooltip>
        <CreateProjectDialog
          project={this.state.editableProject}
          open={this.state.open}
          onClose={this.handleClose.bind(this)}
          newdefaultname={this.state.newDefaultName}
          handleActiveTabChange={this.handleActiveTabChange.bind(this)}
        />
      </div>
    );
  }
}

HomePage.propTypes = {
  classes: PropTypes.object.isRequired,
  history: PropTypes.object,
};

export default withRouter(withStyles(styles)(HomePage));
